import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Container, Element, Heading, Section } from 'react-bulma-components'
import { setTranslations } from 'react-i18nify'
import Layout from '../../components/layout/layout'
import { Translate } from '../../utils/translations'
import { campaign } from '..'
import { useRecordInspection } from './api'
import { altText } from './alt-text'
import ComparisonPair from './comparison-pair'

/**
 * Inspection instructions, ending in the two outcome buttons.
 *
 * The video and the reference images sit where the brief places them: the video
 * introduces the check, and each thumb-press step is followed by the pair
 * showing what a sound and a failed gate look like after that step.
 */

/**
 * Simond's inspection tutorial, "INSPECT & CHECK YOUR ROCKY CARABINER".
 *
 * Hosted on YouTube rather than served from here: the supplied master is HEVC,
 * which Chrome and Firefox cannot reliably decode, and at 57 MB it would be a
 * poor thing to push to someone on mobile data. YouTube transcodes and adapts.
 *
 * `youtube-nocookie.com` avoids setting tracking cookies before the reader has
 * interacted — this campaign has no consent banner, and it is aimed at fifteen
 * EU markets. `rel=0` keeps the end screen to this channel, so a recall page
 * does not finish by suggesting unrelated videos.
 *
 * The source is a Short, so it is 9:16 — which is why the container reserves
 * portrait space rather than the 16:9 the other campaigns use.
 */
const VIDEO_EMBED_URL =
  'https://www.youtube-nocookie.com/embed/mnC4cxZIK-4?rel=0&modestbranding=1'

/**
 * The two outcome buttons.
 *
 * Labels come from the sheet's `BOUTON_OK` / `BOUTON_NOK` rows, so they are
 * translated and country-validated like the rest of the copy — "Mon produit est
 * OK", "Môj produkt NIE JE v poriadku". They replace the hardcoded English
 * placeholders the page shipped with before Simond added those rows.
 */
const OUTCOME_BUTTONS = [
  { outcome: 'ok', labelKey: 'buttons.ok' },
  { outcome: 'nok', labelKey: 'buttons.nok' },
] as const

const SimondProcedurePage: React.FC = () => {
  setTranslations(campaign.presentationLocales)
  const { lang } = useParams()
  const navigate = useNavigate()
  const recordInspection = useRecordInspection()

  /**
   * Record the self-reported inspection result, then show the matching advice.
   *
   * The recording is deliberately not awaited. It is anonymous reporting for
   * Simond, while the advice on the next screen is safety information — a slow
   * or failing request must never delay or withhold it. The request survives
   * this component unmounting.
   *
   * @param outcome - Whether the wire gate stayed seated
   */
  const handleOutcome = (outcome: 'ok' | 'nok') => {
    recordInspection.mutate({
      data: { result: outcome, language: lang ?? 'en' },
    })

    navigate(`/${lang}/result/${outcome}`)
  }

  return (
    <Layout>
      <Element className="simond">
        <Section>
          <Container className="simond__column">
            <Element className="simond__warning" mb={5} data-testid="stop-using-warning">
              <Translate value="procedure.stopUsing" />
            </Element>

            <Heading size={4} className="simond__title" mb={5}>
              <Translate value="procedure.title" />
            </Heading>

            <Element className="simond__video" mb={6} data-testid="inspection-video">
              <iframe
                src={VIDEO_EMBED_URL}
                title={altText('video', lang)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </Element>

            <Element mb={5}>
              <Translate value="procedure.intro" />
            </Element>

            <Element className="simond__step" mb={4} data-testid="procedure-step-1">
              <span className="simond__step-number">1</span>
              <Translate value="procedure.step1" />
            </Element>
            <ComparisonPair
              okImageName="push1_ok"
              nokImageName="push1_nok"
              okAlt={altText('push1Ok', lang)}
              nokAlt={altText('push1Nok', lang)}
            />

            <Element className="simond__step" mb={4} data-testid="procedure-step-2">
              <span className="simond__step-number">2</span>
              <Translate value="procedure.step2" />
            </Element>
            <ComparisonPair
              okImageName="push2_ok"
              nokImageName="push2_nok"
              okAlt={altText('push2Ok', lang)}
              nokAlt={altText('push2Nok', lang)}
            />

            <Element className="simond__outcomes" mt={6}>
              {OUTCOME_BUTTONS.map(({ outcome, labelKey }) => (
                <Button
                  key={outcome}
                  size="large"
                  className={`simond__outcome simond__outcome--${outcome}`}
                  data-testid={`outcome-button-${outcome}`}
                  onClick={() => handleOutcome(outcome)}
                >
                  <Translate value={labelKey} />
                </Button>
              ))}
            </Element>
          </Container>
        </Section>
      </Element>
    </Layout>
  )
}

export default SimondProcedurePage
